import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  IconButton,
  Alert,
  Paper,
  Divider,
  Stack,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  ContentCopy,
  Check,
  Download,
  Share,
  QrCode2,
  Close,
  Link as LinkIcon,
} from '@mui/icons-material';
import { QRCodeCanvas } from 'qrcode.react';

interface PaymentLinkDisplayProps {
  open: boolean;
  onClose: () => void;
  paymentUrl: string;
  factureNumero: string;
  montantTTC: number;
}

const PaymentLinkDisplay: React.FC<PaymentLinkDisplayProps> = ({
  open,
  onClose,
  paymentUrl,
  factureNumero,
  montantTTC,
}) => {
  const [copied, setCopied] = useState(false);
  const [shareSupported, setShareSupported] = useState(false);

  useEffect(() => {
    // Vérifier si l'API Web Share est supportée
    setShareSupported(!!navigator.share);
  }, []);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(paymentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.getElementById('payment-qr-code') as HTMLCanvasElement;
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `qr-code-${factureNumero}.png`;
      link.href = url;
      link.click();
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Paiement de la facture ${factureNumero}`,
          text: `Voici le lien de paiement pour la facture ${factureNumero} d'un montant de ${montantTTC.toFixed(2)} €`,
          url: paymentUrl,
        });
      } catch (err) {
        console.error('Erreur lors du partage:', err);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QrCode2 sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={600}>
              Lien de paiement généré
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Informations de la facture */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'primary.50',
              border: '1px solid',
              borderColor: 'primary.100',
              borderRadius: 2,
            }}
          >
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Facture
                </Typography>
                <Chip
                  label={factureNumero}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Montant à payer
                </Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">
                  {montantTTC.toFixed(2)} €
                </Typography>
              </Box>
            </Stack>
          </Paper>

          <Divider />

          {/* QR Code */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600} textAlign="center">
              Scanner pour payer
            </Typography>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                display: 'inline-flex',
                bgcolor: 'white',
              }}
            >
              <QRCodeCanvas
                id="payment-qr-code"
                value={paymentUrl}
                size={220}
                level="H"
                includeMargin={true}
              />
            </Paper>
            <Alert severity="info" icon={false} sx={{ width: '100%' }}>
              <Typography variant="body2" textAlign="center">
                Scannez ce code QR avec votre smartphone pour accéder au paiement sécurisé
              </Typography>
            </Alert>
          </Box>

          <Divider>ou</Divider>

          {/* Lien de paiement */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={1} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon fontSize="small" />
              Lien de paiement
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                value={paymentUrl}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  }
                }}
                size="small"
              />
              <IconButton
                color={copied ? 'success' : 'primary'}
                onClick={handleCopyLink}
                sx={{
                  bgcolor: copied ? 'success.50' : 'primary.50',
                  '&:hover': {
                    bgcolor: copied ? 'success.100' : 'primary.100',
                  },
                }}
              >
                {copied ? <Check /> : <ContentCopy />}
              </IconButton>
            </Box>
            {copied && (
              <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                Lien copié dans le presse-papiers !
              </Typography>
            )}
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadQR}
            >
              Télécharger QR Code
            </Button>
            {shareSupported && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Share />}
                onClick={handleShare}
              >
                Partager
              </Button>
            )}
          </Stack>

          <Alert severity="success" icon={<Check />}>
            <Typography variant="body2" fontWeight={500}>
              Le QR code sera également inclus dans le PDF de la facture
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="contained" fullWidth size="large">
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentLinkDisplay;
